import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { parseUrlPath } from '../../../../utils/routing';
import { useTabs } from '../../../../context/TabsContext';

/**
 * Initial startup navigation from the current URL (or restored active tab, or default campaign).
 *
 * Waits for persisted tabs to hydrate so the tab bar highlight and the rendered
 * content are restored from the same source of truth. Without this, content came
 * from URL / lastSelectedCampaignId while the highlight came from activeTabId,
 * and the two could disagree on a cold start.
 */
export function useAppStartupFromUrl({
  isUpstreamLoading,
  isInitialStartupComplete,
  setIsInitialStartupComplete,
  startupInProgressRef,
  sprints,
  lastSelectedCampaignId,
  getFirstActiveSprint,
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  handleSidebarNavigate,
  openDocsArea,
  openDashboardsArea,
  setPreferredDetailView,
  handleTabClick,
  navigateToCampaign,
  navigateToTemplate,
  navigateToView,
  navigateToDocs,
  navigateToDashboards,
  navigateToCampaigns,
  navigateToNewTab,
  openCampaignsHomeArea,
  openSettingsModal,
  preferences,
}) {
  const location = useLocation();
  const {
    activeTab,
    isLoaded: tabsLoaded,
    findTab,
    switchToTab,
    showInActiveTab,
    openTab,
    openDocsHomeTab,
    openDashboardsHomeTab,
    openCampaignsHomeTab,
  } = useTabs();

  useEffect(() => {
    if (isUpstreamLoading || !tabsLoaded || isInitialStartupComplete || startupInProgressRef.current) {
      return;
    }

    startupInProgressRef.current = true;

    const performStartup = async () => {
      const parsed = parseUrlPath(location.pathname, location.search);

      // When the URL deep-links to specific content, the highlighted tab must
      // be that place — switch to a match, or rewrite the current chip.
      const syncActiveTabToTarget = (type, targetId, fields = {}) => {
        if (activeTab && activeTab.type === type && activeTab.targetId === targetId) return;
        const match = findTab(type, targetId);
        if (match) {
          switchToTab(match.id);
          return;
        }
        showInActiveTab({
          type,
          targetId,
          label: fields.label,
          icon: fields.icon ?? null,
          viewMode: fields.viewMode ?? null,
          customViewId: fields.customViewId ?? null,
        });
      };

      // The restored active tab can only drive startup if its target still exists.
      const canRestoreActiveTab = () => {
        if (!activeTab) return false;
        if (activeTab.type === 'campaign') {
          return sprints.some(s => s.id === activeTab.targetId && !s.isTemplate && !s.deletedAt);
        }
        if (activeTab.type === 'template') {
          return sprints.some(s => s.id === activeTab.targetId && (s.isTemplate || s.type === 'template') && !s.deletedAt);
        }
        if (activeTab.type === 'doc') {
          return Boolean(activeTab.targetId);
        }
        return true; // view / blank / docs / dashboards / campaigns
      };

      // Navigate content to the restored active tab and sync the URL to it.
      // Campaign/template use the awaited select handlers so selectedSprintId
      // is set before startup completes (tab-click navigation is after-paint).
      const restoreActiveTab = async (tab) => {
        const viewMode = tab.viewMode || preferences.defaultView || 'flow';

        if (tab.type === 'campaign') {
          const campaign = sprints.find(s => s.id === tab.targetId && !s.isTemplate && !s.deletedAt);
          if (campaign) {
            const selectOptions = {
              view: tab.customViewId
                ? { customViewId: tab.customViewId, type: viewMode }
                : viewMode,
            };
            if (tab.openMissionId) {
              selectOptions.missionId = tab.openMissionId;
            }
            await handleSelectSprint(campaign, selectOptions);
            navigateToCampaign(tab.targetId, {
              viewMode,
              customViewId: tab.customViewId || null,
              replace: true,
            });
            return;
          }
        }

        if (tab.type === 'template') {
          const template = sprints.find(
            s => s.id === tab.targetId && (s.isTemplate || s.type === 'template') && !s.deletedAt
          );
          if (template) {
            await handleSelectTemplate(template, { view: viewMode });
            navigateToTemplate(tab.targetId, { viewMode, replace: true });
            return;
          }
        }

        await handleTabClick(tab);
        if (tab.type === 'view') {
          navigateToView(tab.targetId, { viewMode, replace: true });
        } else if (tab.type === 'doc' || tab.type === 'docs') {
          navigateToDocs({ replace: true });
        } else if (tab.type === 'dashboards') {
          navigateToDashboards({ replace: true });
        } else if (tab.type === 'campaigns') {
          navigateToCampaigns({ replace: true });
        }
        // 'blank' — handleTabClick already navigated to the new-tab URL
      };

      if (parsed.type === 'campaign' && parsed.id) {
        const campaign = sprints.find(s => s.id === parsed.id && !s.isTemplate && !s.deletedAt);
        if (campaign) {
          // Older board links dropped /board from the path. The open tab still has it.
          const remembered = (activeTab?.type === 'campaign' && activeTab.targetId === campaign.id)
            ? activeTab
            : findTab('campaign', campaign.id);
          const viewMode = parsed.viewMode || remembered?.viewMode || null;
          const customViewId = parsed.customViewId || remembered?.customViewId || null;

          const selectOptions = {};
          if (customViewId) {
            selectOptions.view = { customViewId, type: viewMode || undefined };
          } else if (viewMode) {
            selectOptions.view = viewMode;
          }
          if (parsed.missionId) {
            selectOptions.missionId = parsed.missionId;
          }

          await handleSelectSprint(campaign, selectOptions);
          syncActiveTabToTarget('campaign', campaign.id, {
            label: campaign.name,
            icon: campaign.icon,
            viewMode: viewMode || preferences.defaultView || 'flow',
            customViewId,
          });
          setIsInitialStartupComplete(true);
          return;
        }
      }

      if (parsed.type === 'template' && parsed.id) {
        const template = sprints.find(s => s.id === parsed.id && (s.isTemplate || s.type === 'template') && !s.deletedAt);
        if (template) {
          if (parsed.viewMode) {
            setPreferredDetailView(parsed.viewMode);
          }
          await handleSelectTemplate(template);
          syncActiveTabToTarget('template', template.id, {
            label: template.name,
            icon: template.icon || '📋',
            viewMode: parsed.viewMode || preferences.defaultView || 'flow',
          });
          setIsInitialStartupComplete(true);
          return;
        }
      }

      if (parsed.type === 'view' && parsed.id) {
        if (parsed.viewMode) {
          setPreferredDetailView(parsed.viewMode);
        }
        await handleSelectView({ id: parsed.id });
        syncActiveTabToTarget('view', parsed.id, {
          label: 'View',
          icon: '📊',
          viewMode: parsed.viewMode || preferences.defaultView || 'flow',
        });
        setIsInitialStartupComplete(true);
        return;
      }

      if (parsed.type === 'docs') {
        if (activeTab?.type === 'doc' && canRestoreActiveTab()) {
          await restoreActiveTab(activeTab);
        } else if (activeTab?.type === 'docs') {
          await restoreActiveTab(activeTab);
        } else {
          openDocsHomeTab();
          openDocsArea?.();
          navigateToDocs({ replace: true });
        }
        setIsInitialStartupComplete(true);
        return;
      }

      if (parsed.type === 'dashboards') {
        if (activeTab?.type === 'dashboards') {
          await restoreActiveTab(activeTab);
        } else {
          openDashboardsHomeTab();
          openDashboardsArea?.();
          navigateToDashboards({ replace: true });
        }
        setIsInitialStartupComplete(true);
        return;
      }

      if (parsed.type === 'campaigns') {
        // /campaigns is the area home URL, but close/reopen and logout land
        // here even when the persisted chip is a campaign (or a doc). Same
        // idea as /docs restoring an open document — don't steal that tab.
        if (canRestoreActiveTab() && activeTab.type !== 'campaigns') {
          await restoreActiveTab(activeTab);
        } else if (activeTab?.type === 'campaigns') {
          await restoreActiveTab(activeTab);
        } else {
          openCampaignsHomeArea?.();
          openCampaignsHomeTab();
          navigateToCampaigns({ replace: true });
        }
        setIsInitialStartupComplete(true);
        return;
      }

      if (parsed.type === 'settings') {
        // Overlay only — restore the last place so a tab still owns the main view.
        openSettingsModal?.();
        if (canRestoreActiveTab()) {
          await restoreActiveTab(activeTab);
        } else {
          openCampaignsHomeArea?.();
          navigateToCampaigns({ replace: true });
        }
        setIsInitialStartupComplete(true);
        return;
      }

      // Blank new-tab URL — restore a content tab if that's what was selected,
      // otherwise make sure a New Tab chip owns this page.
      if (parsed.type === 'blank') {
        if (activeTab && activeTab.type !== 'blank' && canRestoreActiveTab()) {
          await restoreActiveTab(activeTab);
        } else if (activeTab?.type === 'blank') {
          await restoreActiveTab(activeTab);
        } else {
          openTab({
            type: 'blank',
            targetId: null,
            label: 'New Tab',
            icon: null,
          });
          await handleTabClick({ type: 'blank', targetId: null, label: 'New Tab', icon: null });
          navigateToNewTab?.({ replace: true });
        }
        setIsInitialStartupComplete(true);
        return;
      }

      // No deep link (Electron cold start / fresh window): the restored active
      // tab is the source of truth for what was last open
      if (canRestoreActiveTab()) {
        await restoreActiveTab(activeTab);
        setIsInitialStartupComplete(true);
        return;
      }

      let campaignToSelect = null;

      if (lastSelectedCampaignId) {
        const lastSelected = sprints.find(s => s.id === lastSelectedCampaignId && !s.deletedAt && !s.isTemplate);
        if (lastSelected) {
          campaignToSelect = lastSelected;
        }
      }

      if (!campaignToSelect) {
        campaignToSelect = getFirstActiveSprint();
      }

      if (campaignToSelect) {
        await handleSelectSprint(campaignToSelect);
        navigateToCampaign(campaignToSelect.id, {
          viewMode: preferences.defaultView || 'flow',
          replace: true,
        });
        syncActiveTabToTarget('campaign', campaignToSelect.id, {
          label: campaignToSelect.name,
          icon: campaignToSelect.icon,
          viewMode: preferences.defaultView || 'flow',
        });
      } else {
        openCampaignsHomeArea?.();
        navigateToCampaigns({ replace: true });
      }

      setIsInitialStartupComplete(true);
    };

    performStartup().catch((err) => {
      console.error('[useAppStartupFromUrl] Startup failed:', err);
      setIsInitialStartupComplete(true);
    });
  }, [
    isUpstreamLoading, isInitialStartupComplete, sprints, location.pathname, location.search,
    lastSelectedCampaignId, getFirstActiveSprint, handleSelectSprint, handleSelectTemplate,
    handleSelectView, handleSidebarNavigate, openDocsArea, setPreferredDetailView, navigateToCampaign,
    navigateToDocs, navigateToDashboards, preferences.defaultView, setIsInitialStartupComplete,
    startupInProgressRef, tabsLoaded, activeTab, findTab, switchToTab, showInActiveTab, openTab,
    handleTabClick, navigateToTemplate, navigateToView, openDocsHomeTab, openDashboardsArea,
    openDashboardsHomeTab, navigateToCampaigns, navigateToNewTab, openCampaignsHomeArea,
    openCampaignsHomeTab, openSettingsModal,
  ]);
}
